import { computed } from 'mobx'
import type { SelectOption } from '@/components/inputs/select-option'
import type { IValidator } from 'validx'
import type { OrganizerBinding } from '@/features/legends/legend-engine'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { Question } from '@/features/legends/questions/questions'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'

/**
 * Base for elements tied to a personal tax organizer binding. The binding
 * decides the type of the element's formula, and each binding can only be
 * used by one element of a type.
 */
export abstract class OrganizerBindingQuestionEditorViewModelBase<
  TQuestion extends Question,
> extends QuestionEditorViewModelBase<TQuestion> {
  /**
   * The formula, of the binding's type.
   */
  readonly expr: RynoEditorViewModel

  /**
   * @param options
   * @param isExprRequired
   */
  constructor(
    options: QuestionEditorViewModelOptions,
    isExprRequired: boolean,
  ) {
    super(options)
    this.expr = new RynoEditorViewModel({
      required: isExprRequired,
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => this.bindingValueType,
    })
    this.lifecycle.manage(this.expr)
  }

  /**
   * The picked binding's ID.
   */
  abstract get bindingId(): string

  /**
   * Picks the binding.
   */
  abstract setBindingId(id: string): void

  /**
   * The organizer's bindings of the element's kind.
   */
  abstract get catalog(): OrganizerBinding[]

  /**
   * The picked binding, when the catalog has it.
   */
  @computed
  get binding() {
    return this.catalog.find((b) => b.id === this.bindingId) ?? null
  }

  /**
   * The type the formula must return, once a binding is picked.
   */
  @computed
  get bindingValueType() {
    return this.binding?.valueType ?? null
  }

  /**
   * Bindings that can be picked: those no other element of the type uses.
   */
  @computed
  get bindingOptions(): SelectOption[] {
    const used = this.parentQuestionsBuilder.allQuestions.flatMap((q) =>
      (q.type === 'PersonalOrganizerPrefill' ||
        q.type === 'PersonalOrganizerCollectedValue') &&
      q.type === this.type &&
      q.cid !== this.cid
        ? [q.bindingId]
        : [],
    )
    return this.catalog
      .filter((b) => !used.includes(b.id))
      .map((b) => ({ id: b.id, text: b.displayName }))
  }

  /**
   * The symbols the formula can use.
   */
  @computed
  get symbolTable() {
    return this.dependencies.symbols.getSymbolTableFor(this)
  }

  /**
   * Validators for the binding ID field. An unknown binding is only reported
   * once the catalog is loaded.
   *
   * @param kind How the binding is named in messages, like "prefill".
   */
  protected _bindingIdValidators(
    kind: string,
  ): IValidator<OrganizerBindingQuestionEditorViewModelBase<TQuestion>>[] {
    return [
      () => !!this.bindingId || `A ${kind} binding is required.`,
      () =>
        !this.dependencies.symbols.organizerBindings ||
        !!this.binding ||
        `Unknown ${kind} binding.`,
    ]
  }
}
